import {
  AfterViewInit,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzIconModule } from 'ng-zorro-antd/icon';

import { HeaderComponent, Location, NameSlicePipe } from '@lp-ui-lib';
import { AssetsService, I18nService, UiService } from '@lp-ui-lib';
import { SidebarComponent } from '@lp-ui-lib';
import { BreadcrumbsComponent } from '@lp-ui-lib';
import { SearchFilterComponent } from '@lp-ui-lib';
import { AssetNavButtonComponent } from '@lp-ui-lib';
import { CardComponent } from '../../components/card/card';

type ViewportMode = 'mobile' | 'tablet' | 'desktop';

@Component({
  selector: 'app-disabled-assets',
  standalone: true,
  imports: [
    SidebarComponent,
    BreadcrumbsComponent,
    SearchFilterComponent,
    AssetNavButtonComponent,
    NzAvatarModule,
    CardComponent,
    NameSlicePipe,
    NzIconModule,
    HeaderComponent,
  ],
  templateUrl: './disabled-assets.html',
  styleUrls: ['./disabled-assets.scss'],
})
export class DisabledAssetsComponent implements AfterViewInit {
  private readonly assetsService = inject(AssetsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly ui = inject(UiService);
  public readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  // Route id as signal (so effects can depend on it)
  readonly plantID = signal<number>(0);

  readonly plant = signal<Location | null>(null);
  readonly loading = signal<boolean>(true);
  readonly init = signal<boolean>(false);
  readonly emptyResults = signal<boolean>(false);

  readonly filter = signal<any>(null);

  private readonly viewportMode = signal<ViewportMode>(this.getViewportMode());
  readonly isMobile = computed(() => this.viewportMode() === 'mobile');
  readonly isTablet = computed(() => this.viewportMode() === 'tablet');
  readonly isDesktop = computed(() => this.viewportMode() === 'desktop');

  readonly buildings = computed<Location[]>(() => {
    const plant = this.plant();
    if (!plant || this.loading() || !this.init()) return [];

    if (plant.children && plant.children[0]?.isAsset) return [plant];

    return (plant.children ?? []).filter((child: any) =>
      (child.Assets ?? []).some((a: any) => a.isArchived)
    );
  });

  constructor() {
    fromEvent(window, 'resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const next = this.getViewportMode();
        if (next !== this.viewportMode()) this.viewportMode.set(next);
      });

    this.ui.onNavBack(() => this.handleNavBack());

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = Number(params.get('id'));
      this.plantID.set(id);

      // reset UI state for new id
      this.loading.set(true);
      this.emptyResults.set(false);
      this.plant.set(null);

      this.assetsService.resetLocationFilter();
    });

    effect(() => {
      const id = this.plantID();
      if (!id) return;
      const state: any = this.assetsService.plantsState();
      if (!state) {
        this.loading.set(true);
        return;
      }

      // once state arrives, resolve plant
      const newPlant = this.resolvePlantFromState(state, id);

      this.plant.set(newPlant);
      this.emptyResults.set(!newPlant);
      this.loading.set(false);

      if (newPlant) {
        const parent = this.getParentForBackNav((newPlant as any).parentID);
        const parentName =
          parent && parent.curLayer !== 'a' && parent.curLayer !== 'b'
            ? (parent as any).objectName
            : this.i18n.langString('home');

        this.ui.setBackText(this.i18n.parse(parentName));

        this.assetsService.updateSelectionPreset([
          ...((newPlant as any).breadcrumbs ?? []),
          newPlant,
        ]);
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.init.set(true), 0);
  }

  onFilterChanged(event: any) {
    this.filter.set(event);
  }

  // ---------- helpers ----------

  private resolvePlantFromState(state: any, plantID: number): Location | null {
    let newPlant: Location | null =
      state?.plants?.map((group: any) => group.find(plantID))?.find((x: any) => !!x) ?? null;

    // preserve your group-index logic
    if (
      newPlant &&
      (newPlant as any).Assets?.length > 25 &&
      !(newPlant as any).NextLayerIsAboveEquipment
    ) {
      while (
        newPlant &&
        (newPlant as any).children?.length === 1 &&
        !(newPlant as any).NextLayerIsAboveEquipment
      ) {
        newPlant = (newPlant as any).children[0];
      }
    }

    return newPlant;
  }

  private handleNavBack(): void {
    const plant = this.plant();

    if (!plant) {
      this.router.navigate(['home'], { replaceUrl: true });
      return;
    }

    const parentID = (plant as any).parentID;
    let parent: any = this.getParentForBackNav(parentID);
    let nextParent: any = this.assetsService.findLocation(parent?.parentID);

    while (
      nextParent &&
      parent &&
      parent.children?.filter((child: any) => (child.Assets?.length ?? 0) > 0).length === 1 &&
      nextParent.curLayer !== 'a'
    ) {
      parent = nextParent;
      nextParent = this.assetsService.findLocation(nextParent.parentID);
    }

    if (parent && parent.curLayer !== 'a' && parent.curLayer !== 'b') {
      this.router.navigate(['plant', parent.objectID], { replaceUrl: true });
    } else {
      this.router.navigate(['home', 'list'], { replaceUrl: true });
    }
  }

  navigate(building: any) {
    this.router.navigate(['plant', building.objectID]);
  }

  groupBy<T>(arr: T[], key: keyof T): T[][] {
    return Object.values(
      arr.reduce((rv: Record<string, T[]>, x: any) => {
        const k = String(x[key]);
        (rv[k] = rv[k] || []).push(x);
        return rv;
      }, {})
    );
  }

  assetsGroupBy(plant: any) {
    return this.groupBy<any>(plant.Assets ?? [], 'parentID');
  }

  getParentForBackNav(parentID: number): Location | null {
    let parent: any = this.assetsService.findLocation(parentID);

    while (
      parent &&
      parent.curLayer !== 'a' &&
      parent.curLayer !== 'b' &&
      parent.curLayer !== 'c'
    ) {
      if (
        (parent.Assets?.length ?? 0) > 25 &&
        !parent.IsAboveEquipment &&
        !parent.NextLayerIsAboveEquipment
      ) {
        break;
      }
      parent = this.assetsService.findLocation(parent.parentID);
    }

    return parent ?? null;
  }

  private getViewportMode(): ViewportMode {
    if (this.ui.IsDesktop()) return 'desktop';
    if (this.ui.IsTablet()) return 'tablet';
    return 'mobile';
  }
}
